import { useState } from 'react';

import { Button, Dropdown, Table } from 'fluxo-ui';
import { BarChartIcon, DownloadIcon, EyeIcon, FilterIcon } from 'fluxo-ui/icons';

import type { Column, ComponentEvent, ListItem } from 'fluxo-ui';

interface Attempt {
    id: string;
    attemptId: string;
    userId: string;
    userName: string;
    userEmail: string;
    attemptNumber: number;
    startTime: string;
    endTime?: string;
    status: 'in_progress' | 'submitted' | 'auto_submitted' | 'abandoned';
    scoredMarks: number;
    totalMarks: number;
    percentage: number;
    passed: boolean;
    gradingStatus: 'auto_graded' | 'pending_manual' | 'fully_graded';
    timeElapsedSeconds: number;
}

interface ResultsDashboardProps {
    examId?: string;
    examTitle: string;
    totalMarks?: number;
    attempts?: Attempt[];
    loading?: boolean;
    onViewResult?: (attemptId: string) => void;
    onExport?: (format: 'csv' | 'pdf') => Promise<void>;
}

const ResultsDashboard: React.FC<ResultsDashboardProps> = ({ examTitle, attempts = [], loading = false, onViewResult, onExport }) => {
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [gradingFilter, setGradingFilter] = useState<string>('all');
    const [exporting, setExporting] = useState(false);

    const statusOptions: ListItem[] = [
        { label: 'All Status', value: 'all' },
        { label: 'Submitted', value: 'submitted' },
        { label: 'In Progress', value: 'in_progress' },
        { label: 'Auto Submitted', value: 'auto_submitted' },
        { label: 'Abandoned', value: 'abandoned' },
    ];

    const gradingOptions: ListItem[] = [
        { label: 'All Grading Status', value: 'all' },
        { label: 'Auto Graded', value: 'auto_graded' },
        { label: 'Pending Manual', value: 'pending_manual' },
        { label: 'Fully Graded', value: 'fully_graded' },
    ];

    const filteredAttempts = attempts.filter((attempt) => {
        if (statusFilter !== 'all' && attempt.status !== statusFilter) return false;
        if (gradingFilter !== 'all' && attempt.gradingStatus !== gradingFilter) return false;
        return true;
    });

    const getStatusBadge = (status: string) => {
        const styles = {
            submitted: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
            in_progress: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
            auto_submitted: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
            abandoned: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
        };
        return (
            <span className={`px-2 py-1 text-xs rounded-full font-medium ${styles[status as keyof typeof styles]}`}>
                {status.replace(/_/g, ' ')}
            </span>
        );
    };

    const getGradingBadge = (status: string) => {
        const styles = {
            auto_graded: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
            pending_manual: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
            fully_graded: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
        };
        return (
            <span className={`px-2 py-1 text-xs rounded-full font-medium ${styles[status as keyof typeof styles]}`}>
                {status.replace(/_/g, ' ')}
            </span>
        );
    };

    const getScoreColor = (percentage: number) => {
        if (percentage >= 80) return 'text-emerald-600 dark:text-emerald-400';
        if (percentage >= 60) return 'text-amber-600 dark:text-amber-400';
        return 'text-rose-600 dark:text-rose-400';
    };

    const formatTime = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    const handleExport = async (format: 'csv' | 'pdf') => {
        setExporting(true);
        try {
            await onExport?.(format);
        } finally {
            setExporting(false);
        }
    };

    const columns: Column[] = [
        {
            title: 'Attempt ID',
            field: 'attemptId',
            sortable: true,
            cellClassName: 'font-mono text-sm',
            hideBelow: 'lg',
        },
        {
            title: 'Student',
            field: 'userName',
            sortable: true,
            template: (row: Attempt) => (
                <div>
                    <div className="font-medium text-slate-900 dark:text-white">{row.userName}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{row.userEmail}</div>
                </div>
            ),
        },
        {
            title: 'Attempt',
            field: 'attemptNumber',
            sortable: true,
            hideBelow: 'md',
            template: (row: Attempt) => <span className="text-slate-600 dark:text-slate-400">#{row.attemptNumber}</span>,
        },
        {
            title: 'Score',
            field: 'percentage',
            sortable: true,
            template: (row: Attempt) => (
                <div>
                    <div className={`text-lg font-bold ${getScoreColor(row.percentage)}`}>{row.percentage.toFixed(1)}%</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                        {row.scoredMarks}/{row.totalMarks}
                    </div>
                </div>
            ),
        },
        {
            title: 'Status',
            field: 'status',
            sortable: true,
            template: (row: Attempt) => (
                <div className="space-y-1">
                    {getStatusBadge(row.status)}
                    {row.status === 'in_progress' ? (
                        <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">— Not submitted</div>
                    ) : row.passed ? (
                        <div className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">✓ Passed</div>
                    ) : (
                        <div className="text-xs text-rose-600 dark:text-rose-400 font-medium">✗ Failed</div>
                    )}
                </div>
            ),
        },
        {
            title: 'Grading',
            field: 'gradingStatus',
            sortable: true,
            hideBelow: 'md',
            template: (row: Attempt) => getGradingBadge(row.gradingStatus),
        },
        {
            title: 'Time Taken',
            field: 'timeElapsedSeconds',
            sortable: true,
            hideBelow: 'lg',
            template: (row: Attempt) => <span className="text-slate-600 dark:text-slate-400">{formatTime(row.timeElapsedSeconds)}</span>,
        },
        {
            title: 'Submitted',
            field: 'endTime',
            sortable: true,
            hideBelow: 'lg',
            template: (row: Attempt) => (
                <span className="text-sm text-slate-600 dark:text-slate-400">
                    {row.endTime ? new Date(row.endTime).toLocaleString() : '-'}
                </span>
            ),
        },
        {
            title: 'Actions',
            field: 'actions',
            template: (row: Attempt) => (
                <button
                    onClick={() => onViewResult?.(row.id)}
                    className="p-2 text-slate-600 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                    title="View detailed result"
                >
                    <EyeIcon className="w-4 h-4" />
                </button>
            ),
        },
    ];

    // Calculate statistics
    const stats = {
        total: attempts.length,
        submitted: attempts.filter((a) => a.status === 'submitted' || a.status === 'auto_submitted').length,
        passed: attempts.filter((a) => a.passed).length,
        failed: attempts.filter((a) => !a.passed && (a.status === 'submitted' || a.status === 'auto_submitted')).length,
        inProgress: attempts.filter((a) => a.status === 'in_progress').length,
        avgScore: attempts.length > 0 ? attempts.reduce((sum, a) => sum + a.percentage, 0) / attempts.length : 0,
        pendingGrading: attempts.filter((a) => a.gradingStatus === 'pending_manual').length,
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">{examTitle}</h2>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Exam Results Dashboard</p>
                </div>
                <div className="flex items-center gap-3">
                    <Button label="Analytics" leftIcon={BarChartIcon} variant="default" layout="outlined" size="sm" />
                    <Button
                        label="Export CSV"
                        leftIcon={DownloadIcon}
                        variant="default"
                        layout="outlined"
                        size="sm"
                        onClick={() => handleExport('csv')}
                        isLoading={exporting}
                        disabled={exporting || attempts.length === 0}
                    />
                    <Button
                        label="Export PDF"
                        leftIcon={DownloadIcon}
                        variant="default"
                        layout="outlined"
                        size="sm"
                        onClick={() => handleExport('pdf')}
                        isLoading={exporting}
                        disabled={exporting || attempts.length === 0}
                    />
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                {[
                    { label: 'Total Attempts', value: stats.total, dot: 'bg-slate-400', valueCls: 'text-slate-900 dark:text-white' },
                    { label: 'Submitted', value: stats.submitted, dot: 'bg-teal-500', valueCls: 'text-teal-600 dark:text-teal-400' },
                    { label: 'Passed', value: stats.passed, dot: 'bg-emerald-500', valueCls: 'text-emerald-600 dark:text-emerald-400' },
                    { label: 'Failed', value: stats.failed, dot: 'bg-rose-500', valueCls: 'text-rose-600 dark:text-rose-400' },
                    {
                        label: 'In Progress',
                        value: stats.inProgress,
                        dot: 'bg-orange-500',
                        valueCls: 'text-orange-600 dark:text-orange-400',
                    },
                    {
                        label: 'Avg Score',
                        value: `${stats.avgScore.toFixed(1)}%`,
                        dot: 'bg-cyan-500',
                        valueCls: 'text-cyan-600 dark:text-cyan-400',
                    },
                    {
                        label: 'Pending Grading',
                        value: stats.pendingGrading,
                        dot: 'bg-amber-500',
                        valueCls: 'text-amber-600 dark:text-amber-400',
                    },
                ].map((card) => (
                    <div
                        key={card.label}
                        className="p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow"
                    >
                        <div className={`text-2xl font-bold tabular-nums ${card.valueCls}`}>{card.value}</div>
                        <div className="flex items-center gap-1.5 mt-1">
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${card.dot}`} aria-hidden />
                            <span className="text-xs text-slate-500 dark:text-slate-400">{card.label}</span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-4">
                <div className="flex items-center gap-3 mb-4">
                    <FilterIcon className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                    <h3 className="font-semibold text-slate-900 dark:text-white">Filters</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Dropdown
                        options={statusOptions}
                        value={statusFilter}
                        onChange={(e: ComponentEvent<string>) => setStatusFilter(e.value)}
                        placeholder="Filter by status"
                    />
                    <Dropdown
                        options={gradingOptions}
                        value={gradingFilter}
                        onChange={(e: ComponentEvent<string>) => setGradingFilter(e.value)}
                        placeholder="Filter by grading status"
                    />
                </div>
                {(statusFilter !== 'all' || gradingFilter !== 'all') && (
                    <div className="mt-3 flex items-center justify-between">
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                            Showing {filteredAttempts.length} of {attempts.length} results
                        </span>
                        <Button
                            label="Clear Filters"
                            variant="default"
                            layout="plain"
                            size="sm"
                            onClick={() => {
                                setStatusFilter('all');
                                setGradingFilter('all');
                            }}
                        />
                    </div>
                )}
            </div>

            {/* Results Table */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                <Table
                    columns={columns}
                    rows={filteredAttempts}
                    totalRows={filteredAttempts.length}
                    isLoading={loading}
                    pagination
                    rowsPerPage={20}
                    onRowClick={({ row }) => onViewResult?.(row.id)}
                    noRowsMessage="No exam attempts found"
                />
            </div>
        </div>
    );
};

export default ResultsDashboard;
